import { useCallback, useLayoutEffect, useRef } from 'react';

import _ from 'lodash';

import { getAnchoredScrollTop, getReadingAnchor } from '../DocumentView.utils';

// Keeps the page being read at the same relative offset across zoom, rotation and a saved-page
// swap. The anchor is captured on user scrolls and restored in the commit that resizes the pages.
export const useReadingAnchor = ({
  containerRef,
  isPagesRendered,
  pdf,
  scale,
  rotationByIndex,
  pageOrder,
  onRestore,
}) => {
  const anchorRef = useRef(null);
  const restoredScrollTopRef = useRef(null);
  const isPagesRenderedRef = useRef(isPagesRendered);

  const setReadingAnchor = useCallback(anchor => {
    anchorRef.current = anchor;
  }, []);

  // Scroll events are async, so a restore is recognized by the scroll position it left behind
  // rather than by a flag that a later user scroll could consume.
  const checkIsRestoreScroll = useCallback(() => {
    const isRestoreScroll =
      !_.isNil(restoredScrollTopRef.current) &&
      containerRef.current?.scrollTop === restoredScrollTopRef.current;

    if (!isRestoreScroll) {
      restoredScrollTopRef.current = null;
    }

    return isRestoreScroll;
  }, [containerRef]);

  const captureReadingAnchor = useCallback(() => {
    if (!isPagesRenderedRef.current) {
      return;
    }

    anchorRef.current = getReadingAnchor(containerRef.current);
  }, [containerRef]);

  const onReadingAnchorScroll = useCallback(() => {
    if (checkIsRestoreScroll()) {
      return;
    }

    captureReadingAnchor();
  }, [checkIsRestoreScroll, captureReadingAnchor]);

  useLayoutEffect(() => {
    isPagesRenderedRef.current = isPagesRendered;
  }, [isPagesRendered]);

  // isPagesRendered is a trigger too: a superseded load can keep the loader on past the
  // commit of the new document, so its pages may render a commit later.
  useLayoutEffect(() => {
    const container = containerRef.current;
    const anchor = anchorRef.current;

    if (!isPagesRendered || !container || !anchor) {
      return;
    }

    const scrollTop = getAnchoredScrollTop(container, anchor);

    if (_.isNil(scrollTop)) {
      return;
    }

    container.scrollTop = scrollTop;
    restoredScrollTopRef.current = container.scrollTop;
    onRestore(anchor.readingPageIndex);
  }, [containerRef, onRestore, isPagesRendered, pdf, scale, rotationByIndex]);

  useLayoutEffect(() => {
    captureReadingAnchor();
  }, [captureReadingAnchor, pageOrder]);

  return { setReadingAnchor, checkIsRestoreScroll, onReadingAnchorScroll };
};
